<template>
    <section :class="backgroundColor ? `lifestyle-container bg-${backgroundColor}` : `lifestyle-container`">
        <div class="lifestyle-header">
            <span v-if="eyebrow" :class="eyebrowColor ? `eyebrow text-${eyebrowColor}` : `eyebrow`">
                {{ eyebrow }}
            </span>
            <span v-if="heading" :class="headingColor ? `headline-xl text-${headingColor}` : `headline-xl`" role="heading" aria-level="2">
                {{ heading }}
            </span>
            <Link v-if="cta && getCtaLink(cta).href" :href="getCtaLink(cta).href" :class="cta.style" :aria-label="cta.aria_label">
                {{ cta.label }}
            </Link>
        </div>
        <div class="lifestyle-img">
            <img v-if="image" :src="optimizedImageUrl" :alt="imageAlt" />
        </div>
        <div v-if="props.collapseToGrid" class="lifestyle-palette">
            <template v-for="(card, index) in cardDetails.slice(0, 4)" :key="index">
                    <Link as="div" :href="card.button.link" class="lifestyle-item" :aria-label="`View ${card.title}`" v-if="card.image">
                    <img
                        class="lifestyle-image"
                        :src="card.imageSrc"
                        :srcset="card.imageSrcset"
                        :sizes="card.imageSizes"
                        :alt="card.title"
                    />
                    <div class="item-content">
                        <div class="item-content-inner">
                            <div class="item-price-hidden" v-if="is_dealer">
                                <span class="price">View Item to See Your Pricing</span>
                            </div>
                            <div class="item-price" v-else>
                                from<span class="price">${{ card.description }}</span>
                            </div>
                            <div class="item-specific">{{ card.title }}</div>
                        </div>
                    </div>
                </Link>
            </template>
        </div>
        <div v-else class="lifestyle-palette mobile-swiper">
            <vue-horizontal ref="carouselRef" responsive scroll>
                <template v-for="(card, index) in cardDetails.slice(0, 4)" :key="index">
                    <Link as="div" :href="card.button.link" class="lifestyle-item" :aria-label="`View ${card.title}`" v-if="card.image">
                        <img
                            class="lifestyle-image"
                            :src="card.imageSrc"
                            :srcset="card.imageSrcset"
                            :sizes="card.imageSizes"
                            :alt="card.title"
                        />
                        <div class="item-content">
                            <div class="item-content-inner">
                                <div class="item-price-hidden" v-if="is_dealer">
                                    <span class="price">View Item to See Your Pricing</span>
                                </div>
                                <div class="item-price" v-else>
                                    from<span class="price">${{ card.description }}</span>
                                </div>
                                <div class="item-specific">{{ card.title }}</div>
                            </div>
                        </div>
                    </Link>
                </template>
                <template v-slot:btn-next><span></span></template>
                <template v-slot:btn-prev><span></span></template>
            </vue-horizontal>
        </div>
    </section>
</template>
<script lang="ts" setup>
    import { computed, ref, toRefs, inject, type PropType } from "vue";
    import { useCampaign } from "@/helpers/composables/searchSpringApi";
    import SearchSpringApi from "@/helpers/api/SearchSpringAPI.ts";
    import { Link } from "@inertiajs/vue3";
    import VueHorizontal from "vue-horizontal";
    import type { Cta } from "@/helpers/builder-init/index";
    import { getCtaLink } from "@/helpers/interfaces/Cta";
    import useImageOptimization from "@/helpers/composables/useImageOptimization";
    import { storeToRefs } from "pinia";
    import { useSettingsStore } from '@/store/settings';
    import { comingSoonImages } from "@/helpers/composables/comingSoonImages";
    import { productUploadImageCardResponsive } from "@/helpers/productUploadImageUrl";
    let settingsStore = useSettingsStore();
    let { is_dealer } = storeToRefs(settingsStore);

    let route = inject("route");

    let props = defineProps({
        eyebrow: {
            type: String,
            required: false,
            default: ""
        },
        eyebrowColor: {
            type: String,
            default: ""
        },
        heading: {
            type: String,
            default: ""
        },
        headingColor: {
            type: String,
            default: ""
        },
        image: {
            type: String,
            retquired: false,
            default: ""
        },
        imageAlt: {
            type: String,
            required: false,
            default: "product collection"
        },
        backgroundColor: {
            type: String,
            required: false,
            default: ""
        },
        tag: {
            type: String,
            required: false,
            default: ""
        },
        collapseToGrid: {
            type: Boolean,
            required: true,
            default: false
        },
        cta: {
            type: Object as PropType<Cta>,
            default() {
                return {  };
            }
        }
    });
    let { tag } = toRefs(props);
    const searchSpringApi = new SearchSpringApi();
    const filters = computed(()=>{
        return [
            {
                field: 'advertising_restricted',
                value: 0
            }
        ];
    })
    let { data } = useCampaign({
        searchSpringApi,
        tag,
        filters
    });

    let cardDetails = computed(() => {
        return data.value.results.map((product) => {
            const image = product?.imageUrl ?? comingSoonImages().small;
            const imageStr = typeof image === "string" ? image : "";
            const pack = imageStr
                ? productUploadImageCardResponsive(imageStr)
                : null;
            return {
                image,
                imageSrc: pack?.mediaSrc ?? image,
                imageSrcset: pack?.srcSet,
                imageSizes: pack?.sizes,
                title: product.name,
                description: product.price,
                button: {
                    text: "Shop Now",
                    link: route("productDetail.show", [product.seo_name])
                }
            };
        }).filter((product) => !!product.image);
    });

    let { optimizedImageUrl } = useImageOptimization(props.image);
</script>

